import { $, el } from '../state/store.js';
import { histOn, pushHistory } from '../state/history.js';
import { CATEGORIES, FONTS } from '../core/fonts.js';
import { snapFor, uploadedFamilies, weightsFor } from '../scene/fonts.js';
import { addFontFile, familyFromFilename, weightFromFilename } from '../services/fonts.js';
import { toast } from './toast.js';
import { checkRow, colorRow, numRow, panel, readout, selectRow, sliderRow, stackRow } from '../ui/controls.js';

/* The text inspector, lifted out of rail-right.js.

   It was ~50 lines there and the Effects panel adds another ~90, which would
   have pushed the largest file in ui/ past 530 lines for one object type that
   nothing else shares. Everything it needs is passed in — `rebuild` and
   `renderRight` are closures from renderRight — so there is no import back into
   the rail and no cycle. */

/* Every effect change means a full syncNode: a retrace, an extrude, and for the
   outline a SECOND trace of the whole string. At `fine` detail on a long line
   that is well past a frame, so dragging a slider has to coalesce or it stutters
   badly. The text field already debounces at 220 ms for exactly this reason. */
const REBUILD_MS = 180;
let soonTimer = null;
const debounced = fn => { clearTimeout(soonTimer); soonTimer = setTimeout(fn, REBUILD_MS); };

/* ── uploading a font ────────────────────────────────────────────────────────
   The family name is asked for rather than read out of the file. woff2 tables
   are Brotli-compressed and DecompressionStream has no brotli, so recovering the
   real name would mean shipping a Brotli decoder for a string — and the FontFace
   API lets us name the family whatever we like anyway. Prefill from the filename
   and let the user correct it. */

function openFontUpload(ctx){
  const input = $('#fileFont');
  input.onchange = async ev => {
    const file = ev.target.files[0];
    ev.target.value = '';
    if(!file) return;

    const guess = familyFromFilename(file.name);
    const family = (prompt(
      'Name this typeface.\n\nThis is how it will appear in the menu, and how the project refers to it.',
      guess) || '').trim();
    if(!family) return;

    const w = weightFromFilename(file.name);
    const weight = +(prompt(
      `Which weight is this file?\n\n100 Thin · 400 Regular · 700 Bold · 900 Black\n\n`+
      `Upload the same family again at another weight to add it.`, String(w)) || w) || w;

    try{
      await addFontFile(file, family, weight);
      ctx.o.params.font = family;
      ctx.o.params.weight = weight;
      ctx.rebuild();
      ctx.renderRight();
      pushHistory();
      toast(`“${family}” added at weight ${weight}.`);
    }catch(err){
      console.error(err);
      toast(err.message || 'That file could not be read as a font.', true);
    }
  };
  input.click();
}

/* ── geometry panel rows ─────────────────────────────────────────────────── */

export function textGeometryRows(body, ctx){
  const { o, rebuild, renderRight } = ctx;
  const P = o.params;
  const flat = P.mode === '2d';

  const ti = el('textarea','inp');
  ti.rows = 3;
  ti.spellcheck = false;
  ti.value = P.text == null ? '' : P.text;
  let tTimer;
  ti.addEventListener('input',()=>{
    P.text = ti.value;
    clearTimeout(tTimer); tTimer = setTimeout(rebuild, 220);   // tracing is too slow per keystroke
  });
  histOn(ti);
  body.appendChild(stackRow('Text — Enter for a new line', ti));

  body.appendChild(selectRow('Mode', P.mode || '3d', [
    ['3d','3D — extruded solid'],
    ['2d','2D — flat graphic']
  ], v=>{ P.mode = v; rebuild(); renderRight(); }));

  /* Grouped by category, with the user's own uploads last. Every entry is a
     bundled or uploaded face — no system fonts, because the geometry is TRACED
     from whatever the machine has and Impact on a Mac is a different mesh from
     Impact on Windows. A project has to open the same everywhere. */
  const mine = uploadedFamilies();
  const opts = [];
  for(const cat of CATEGORIES){
    const inCat = FONTS.filter(f => f.category === cat);
    if(!inCat.length) continue;
    opts.push([null, cat]);
    for(const f of inCat) opts.push([f.family, f.family]);
  }
  if(mine.length){
    opts.push([null, 'Your fonts']);
    for(const u of mine) opts.push([u.family, u.family]);
  }
  body.appendChild(selectRow('Typeface', P.font, opts, v=>{
    P.font = v;
    // Carry the weight over only if the new family has it; otherwise take the nearest.
    P.weight = snapFor(v, P.weight);
    rebuild(); renderRight();
  }));

  /* Only the weights this family actually ships. Asking canvas for one it does
     not have gets a synthesised face — strokes thickened by the rasteriser
     rather than drawn that way — and it is that synthesis which gets traced.
     The old menu offered Light for every family regardless. */
  const WEIGHT_NAMES = {100:'Thin',200:'Extra light',300:'Light',400:'Regular',
                        500:'Medium',600:'Semibold',700:'Bold',800:'Extra bold',900:'Black'};
  const ws = weightsFor(P.font);
  if(ws.length > 1){
    body.appendChild(selectRow('Weight', P.weight, ws.map(w => [String(w), WEIGHT_NAMES[w] || String(w)]),
      v=>{P.weight = +v; rebuild();}));
  }

  /* Em, thousandths — the Illustrator/InDesign convention, not this app's usual
     "% of cap height" (which the effect sizes below use): tracking has an
     established unit and matching it beats internal consistency here. Built
     character-by-character in the rasteriser rather than the canvas
     `letterSpacing` property, whose browser support is uneven enough that an
     unsupported value would be a silent no-op. */
  body.appendChild(sliderRow('Tracking', P.tracking||0, -100, 400, 5, v=>{P.tracking=v; rebuild();}, 'em/1000'));

  const fontBtn = el('button','btn btn--sm btn--gh btn--w','Upload a font file…');
  fontBtn.onclick = () => openFontUpload(ctx);
  body.appendChild(fontBtn);

  const lineCount = String(P.text||'').replace(/\r/g,'').split('\n').filter(t=>t.trim()).length;
  if(lineCount > 1){
    body.appendChild(selectRow('Align', P.align||'center', [
      ['left','Left'],['center','Centred'],['right','Right']
    ], v=>{P.align=v; rebuild();}));
    body.appendChild(sliderRow('Line height', P.lineHeight==null?1.08:P.lineHeight, .5, 2.2, .01,
      v=>{P.lineHeight=v; rebuild();}));
  }
  body.appendChild(numRow('Cap height', P.capH, .01, v=>{P.capH=v; rebuild(); renderRight();}, 'm'));

  // Extrusion is what 3D mode IS; in 2D none of it applies.
  if(!flat){
    body.appendChild(numRow('Extrusion', P.depth, .01, v=>{P.depth=Math.max(0,v); rebuild(); renderRight();}, 'm'));
    body.appendChild(numRow('Bevel', P.bevel==null?0:P.bevel, .002, v=>{P.bevel=Math.max(0,v); rebuild(); renderRight();}, 'm'));
    body.appendChild(sliderRow('Smoothing', P.smooth==null?35:P.smooth, 0, 70, 1, v=>{P.smooth=v; rebuild();}));
  }

  body.appendChild(selectRow('Outline detail', P.detail||'normal', [
    ['coarse','Coarse — fastest'],['normal','Normal'],['fine','Fine — smoothest curves']
  ], v=>{P.detail=v; rebuild(); renderRight();}));

  const info = o.node && o.node.userData.info;
  if(info){
    body.appendChild(readout([
      ['Lines', String(lineCount)]
    ]));
    if(!flat && info.clampedBevel)
      body.appendChild(el('p','note note--warn','The bevel is clamped — it can’t exceed 45% of the extrusion or 9% of the cap height, or thin strokes fold in on themselves.'));
  }
}

/* ── surface panel rows ──────────────────────────────────────────────────── */

export function textSurfaceRows(body, ctx){
  const { o, rebuild, renderRight } = ctx;
  if(o.params.mode === '2d') return;         // no side walls to colour

  body.appendChild(el('div','rule'));
  body.appendChild(checkRow('Different extrude colour', !!o.sideOn, v=>{
    o.sideOn = v; rebuild(); renderRight();
  }));
  if(o.sideOn){
    body.appendChild(colorRow('Extrude colour', o.sideColor||'#101010', v=>{ o.sideColor=v; rebuild(); }));
    body.appendChild(sliderRow('Extrude rough', o.sideRough==null?o.roughness:o.sideRough, 0,1,.01,
      v=>{ o.sideRough=v; rebuild(); }));
    body.appendChild(el('p','note','The side walls of the extrusion (its depth) take this colour; the face keeps the colour above.'));
  }
}

/* ── effects panel ───────────────────────────────────────────────────────── */

/**
 * Outline, drop shadow and outer glow — for type AND for pictures.
 *
 * Sizes are a percentage of cap height for text and of the object's height for
 * a picture, which is the nearest thing it has to one: either way, resizing
 * never means re-tuning the effects, and the same numbers give the same halo on
 * both. What differs is only what casts — glyphs, or the silhouette worked out
 * in geometry.js's buildBitmapEffects.
 *
 * @returns {HTMLElement} the panel
 */
export function effectsPanel(ctx){
  const { o, rebuild, renderRight } = ctx;
  const P = o.params;
  const pic = o.type === 'image' || o.type === 'video';
  const it = pic ? 'the picture' : 'the type';
  const p = panel('Effects');
  const B = p.body;
  const soon = () => debounced(rebuild);

  /* ── outline ── */
  B.appendChild(checkRow('Outline', !!P.fxOutline, v=>{ P.fxOutline=v; rebuild(); renderRight(); }));
  if(P.fxOutline){
    B.appendChild(sliderRow('Width', P.fxOutlineW==null?6:P.fxOutlineW, .5, 40, .5, v=>{P.fxOutlineW=v; soon();}, '%'));
    B.appendChild(colorRow('Outline colour', P.fxOutlineColor||'#000000', v=>{P.fxOutlineColor=v; rebuild();}));
    B.appendChild(el('p','note', o.type==='text'
      ? 'Real traced geometry, not a bitmap — it stays crisp however far you zoom, and in 3D it extrudes with the letters so the rim has depth too.'
      : pic
      ? 'Stamped around the silhouette rather than stroked along a contour — there is no traced outline to follow on a picture. A cut-out PNG gets a rim shaped like the thing in it; anything opaque gets a border.'
      : 'Painted as a stroke behind the fill, at whatever resolution the layout picked — same idiom as the shadow and glow below.'));
  }

  B.appendChild(el('div','rule'));

  /* ── shadow ── */
  B.appendChild(checkRow('Drop shadow', !!P.fxShadow, v=>{ P.fxShadow=v; rebuild(); renderRight(); }));
  if(P.fxShadow){
    B.appendChild(sliderRow('Offset X', P.fxShadowDX==null?4:P.fxShadowDX, -50, 50, .5, v=>{P.fxShadowDX=v; soon();}, '%'));
    B.appendChild(sliderRow('Offset Y', P.fxShadowDY==null?-4:P.fxShadowDY, -50, 50, .5, v=>{P.fxShadowDY=v; soon();}, '%'));
    B.appendChild(sliderRow('Blur', P.fxShadowBlur==null?6:P.fxShadowBlur, 0, 60, .5, v=>{P.fxShadowBlur=v; soon();}, '%'));
    B.appendChild(colorRow('Shadow colour', P.fxShadowColor||'#000000', v=>{P.fxShadowColor=v; rebuild();}));
    B.appendChild(sliderRow('Shadow opacity', P.fxShadowOpacity==null?.55:P.fxShadowOpacity, 0, 1, .01, v=>{P.fxShadowOpacity=v; soon();}));
  }

  B.appendChild(el('div','rule'));

  /* ── glow ── */
  B.appendChild(checkRow('Outer glow', !!P.fxGlow, v=>{ P.fxGlow=v; rebuild(); renderRight(); }));
  if(P.fxGlow){
    B.appendChild(sliderRow('Size', P.fxGlowSize==null?12:P.fxGlowSize, 1, 80, .5, v=>{P.fxGlowSize=v; soon();}, '%'));
    B.appendChild(colorRow('Glow colour', P.fxGlowColor||'#F2C300', v=>{P.fxGlowColor=v; rebuild();}));
    B.appendChild(sliderRow('Glow opacity', P.fxGlowOpacity==null?.8:P.fxGlowOpacity, 0, 1, .01, v=>{P.fxGlowOpacity=v; soon();}));
  }

  if(P.fxShadow || P.fxGlow){
    B.appendChild(el('p','note','Shadow and glow are painted into one bitmap behind ' + it + ', so they blur exactly as they would in a design tool. They travel with the object — in Flat output and On-site they take the same anamorphic distortion as everything else, which is what makes them read correctly from the marked vantage point.'));
    if(o.type === 'image')
      B.appendChild(el('p','note','The shape comes from the image itself, so a transparent PNG throws a halo shaped like the thing in it. A mask is followed too. Anything opaque and unmasked casts as its rectangle, which is an ordinary drop shadow on a photograph.'));
    if(o.type === 'video')
      B.appendChild(el('p','note','A clip casts as its PANEL — the mask if it has one, the rectangle otherwise — not as the moving picture inside it. Its alpha changes every frame, and reblurring a bitmap at frame rate would cost more than the effect is worth.'));
    if(o.opacity < 1)
      B.appendChild(el('p','note note--warn','The fill is semi-transparent, so the shadow shows through ' + it + ' rather than being hidden by it.'));
  }
  return p;
}
